import { useId, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  ChevronDown as ChevronDownIcon,
  ExternalLink as ArrowOutIcon,
  RotateCcw as RotateCcwIcon,
  UserPlus as UserPlusIcon,
  Users as UsersIcon,
  type LucideIcon,
} from "lucide-react";
import type {
  ConversationListItemDto,
  ConversationStatusName,
  ContactState,
  MessageOriginatingSocialCommentDto,
} from "@multi-chat/shared";
import { CommentOriginBanner } from "../comments/CommentOriginBanner.js";
import { ChannelIdentityBadge } from "../../../components/channel/ChannelIdentityBadge.js";
import { channelLabelI18nKey } from "../../../components/channel/channel-presentation.js";
import { LtrValue } from "../../../components/common/LtrValue.js";
import { Badge } from "../../../components/ui/badge/index.js";
import { Button } from "../../../components/ui/button/index.js";
import { cn } from "../../../lib/utils.js";
import { ContactChannelIdentityRow } from "../ContactChannelIdentityRow.js";
import { ConversationAvatar } from "../ConversationAvatar/ConversationAvatar.js";
import { linkedContactIdentities } from "../conversation-contact-identities.js";
import { replyWindowTone } from "../conversation-header-timing.js";
import { formatConversationFullDate, formatConversationRelativeDateTime } from "../conversation-time.js";
import { conversationTitle, isPhoneLikeDisplay } from "../inbox-display.js";
import { INBOX_SCROLLER_CLASS } from "../inbox-surface-classes.js";
import type { SavedContactDetailState } from "../use-saved-contact-detail.js";
import { useNowMs } from "../use-now-ms.js";
import {
  buildConversationDetailsModel,
  type CrmContactField,
  type ProviderIdentityField,
} from "./conversation-details-model.js";

const CONTACT_ROW_LABEL_KEY: Record<ProviderIdentityField | CrmContactField, string> = {
  phone: "inbox.details.fields.phone",
  username: "inbox.details.fields.username",
  platformDisplayName: "inbox.details.fields.platformDisplayName",
  firstName: "inbox.details.fields.firstName",
  lastName: "inbox.details.fields.lastName",
};

type DetailsTone = "ok" | "muted" | "warn";

const DETAILS_TONE_CLASS: Record<DetailsTone, string> = {
  ok: "bg-[#def5e7] text-[#13764e]",
  muted: "bg-[#f1f4f3] text-[#5c6c68]",
  warn: "bg-[#fff3df] text-inbox-draft",
};

type DetailsSectionKey = "conversation" | "contact" | "groups" | "linked" | "window";

function defaultOpenSections(): Record<DetailsSectionKey, boolean> {
  return {
    conversation: true,
    contact: false,
    groups: false,
    linked: false,
    window: true,
  };
}

/** Groups and linked identities need an active saved contact. */
function unsavedContactNoteKey(section: "groups" | "linked", state: Exclude<ContactState, "ACTIVE">): string {
  switch (state) {
    case "NOT_SAVED":
      return `inbox.details.${section}.unsaved`;
    case "DELETED":
      return `inbox.details.${section}.deleted`;
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

function contactStateLabelKey(state: ContactState): string {
  switch (state) {
    case "NOT_SAVED":
      return "inbox.details.contactState.NOT_SAVED";
    case "ACTIVE":
      return "inbox.details.contactState.ACTIVE";
    case "DELETED":
      return "inbox.details.contactState.DELETED";
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

function conversationStatusLabelKey(status: ConversationStatusName): string {
  switch (status) {
    case "OPEN":
      return "inbox.details.conversationStatus.OPEN";
    case "PENDING":
      return "inbox.details.conversationStatus.PENDING";
    case "CLOSED":
      return "inbox.details.conversationStatus.CLOSED";
    case "ARCHIVED":
      return "inbox.details.conversationStatus.CLOSED";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function contactStateTone(state: ContactState): DetailsTone {
  switch (state) {
    case "NOT_SAVED":
      return "muted";
    case "DELETED":
      return "warn";
    case "ACTIVE":
      return "ok";
    default: {
      const _exhaustive: never = state;
      return _exhaustive;
    }
  }
}

function conversationStatusTone(status: ConversationStatusName): DetailsTone {
  switch (status) {
    case "OPEN":
      return "ok";
    case "PENDING":
      return "warn";
    case "CLOSED":
    case "ARCHIVED":
      return "muted";
    default: {
      const _exhaustive: never = status;
      return _exhaustive;
    }
  }
}

function DetailsBadge({ tone, children }: { tone: DetailsTone; children: ReactNode }): React.JSX.Element {
  return (
    <Badge className={cn("h-5 border-0 px-2 py-0 text-[0.71875rem] font-semibold", DETAILS_TONE_CLASS[tone])}>
      {children}
    </Badge>
  );
}

function DetailsRow({ label, children }: { label: string; children: ReactNode }): React.JSX.Element {
  return (
    <div className="grid min-h-8 grid-cols-[104px_minmax(0,1fr)] items-center gap-2 py-0.5">
      <dt className="m-0 text-[0.78125rem] text-inbox-muted">{label}</dt>
      <dd className="m-0 flex min-w-0 items-center gap-1.5 text-[0.8125rem] font-[480] break-words text-inbox-strong">
        {children}
      </dd>
    </div>
  );
}

function DetailsMissingValue(): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <span className="text-inbox-subtle">
      <span aria-hidden="true">—</span>
      <span className="sr-only">{t("inbox.details.notAvailable")}</span>
    </span>
  );
}

/** LTR values stay at the row's inline start (right in Hebrew) while keeping their own character order. */
function DetailsValue({ value, ltr }: { value: string; ltr: boolean }): React.JSX.Element {
  if (ltr) {
    return (
      <LtrValue className="min-w-0 truncate tabular-nums" title={value}>
        {value}
      </LtrValue>
    );
  }
  return (
    <bdi className="min-w-0 truncate" title={value}>
      {value}
    </bdi>
  );
}

function DetailsFieldRow({
  label,
  value,
  ltr,
}: {
  label: string;
  value: string | null;
  ltr: boolean;
}): React.JSX.Element {
  return (
    <DetailsRow label={label}>
      {value == null ? <DetailsMissingValue /> : <DetailsValue value={value} ltr={ltr} />}
    </DetailsRow>
  );
}

function DetailsSubheading({ children }: { children: ReactNode }): React.JSX.Element {
  return (
    <p className="m-0 mt-2.5 mb-0.5 text-[0.6875rem] font-semibold text-inbox-subtle">{children}</p>
  );
}

function DetailsSection({
  title,
  count,
  open,
  onToggle,
  children,
}: {
  title: string;
  count?: number;
  open: boolean;
  onToggle: () => void;
  children: ReactNode;
}): React.JSX.Element {
  const bodyId = useId();
  return (
    <section className="border-b border-inbox-line-soft">
      <button
        type="button"
        className="flex h-[42px] w-full items-center gap-2 px-4 text-start text-[0.78125rem] font-semibold text-inbox-strong hover:bg-inbox-hover focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-primary"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={onToggle}
      >
        <span className="min-w-0 truncate">{title}</span>
        {count ? (
          <span className="shrink-0 rounded-full bg-[#f1f5f4] px-1.5 text-[0.6875rem] leading-[17px] font-semibold text-inbox-subtle tabular-nums">
            {count}
          </span>
        ) : null}
        <ChevronDownIcon
          size={16}
          className={cn(
            "ms-auto shrink-0 text-inbox-subtle transition-transform duration-200 ease-inbox motion-reduce:transition-none",
            open && "rotate-180",
          )}
          aria-hidden="true"
        />
      </button>
      <div
        id={bodyId}
        className={cn(
          "grid transition-[grid-template-rows] duration-300 ease-inbox motion-reduce:transition-none",
          open ? "grid-rows-[1fr]" : "grid-rows-[0fr]",
        )}
        inert={!open || undefined}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="px-4 pb-3">{children}</div>
        </div>
      </div>
    </section>
  );
}

function DetailsNote({ icon: Icon, children }: { icon?: LucideIcon; children: ReactNode }): React.JSX.Element {
  if (Icon == null) {
    return <p className="m-0 text-[0.75rem] leading-relaxed text-inbox-muted">{children}</p>;
  }
  return (
    <div className="flex items-center gap-2.5 rounded-[10px] border border-dashed border-[#d3dfdb] px-3 py-2.5 text-[0.78125rem] leading-normal text-inbox-muted">
      <Icon size={16} className="shrink-0 text-inbox-subtle" aria-hidden="true" />
      <span className="min-w-0">{children}</span>
    </div>
  );
}

function DetailsTimestamp({ iso, nowMs }: { iso: string; nowMs: number }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  return (
    <time
      dateTime={iso}
      title={formatConversationFullDate(iso, i18n.language)}
      className="min-w-0 truncate tabular-nums"
    >
      {formatConversationRelativeDateTime(iso, i18n.language, nowMs, t)}
    </time>
  );
}

function ContactDetailSkeleton({ shape }: { shape: "chips" | "rows" }): React.JSX.Element {
  const block = "animate-pulse rounded bg-inbox-line-soft motion-reduce:animate-none";
  if (shape === "chips") {
    return (
      <div className="flex gap-1.5" aria-hidden="true">
        <span className={cn(block, "h-[26px] w-20 rounded-full")} />
        <span className={cn(block, "h-[26px] w-14 rounded-full")} />
      </div>
    );
  }
  return (
    <div className="flex items-center gap-2.5 py-2" aria-hidden="true">
      <span className={cn(block, "size-[22px] rounded-full")} />
      <span className={cn(block, "h-3 flex-1")} />
    </div>
  );
}

function ContactDetailError({ onRetry }: { onRetry?: () => void }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[0.75rem] text-inbox-muted" role="status">
      <span>{t("inbox.details.contactLoadError")}</span>
      {onRetry ? (
        <button
          type="button"
          className="rounded-md px-1 py-0.5 text-[0.75rem] font-[650] text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
          onClick={onRetry}
        >
          {t("inbox.retry")}
        </button>
      ) : null}
    </div>
  );
}

export function ConversationDetailsContent({
  conversation,
  customerServiceWindowExpiresAt,
  lastInboundCustomerMessageAt,
  metaWindowOpen,
  connectedAccountName,
  contactActionPending = false,
  onSaveContact,
  onRestoreContact,
  contactDetail = { kind: "none" },
  onRetryContactDetail,
  onOpenConversation,
  canManageContacts = false,
  assigneeControl,
  originatingSocialComment = null,
  className,
}: {
  conversation: ConversationListItemDto;
  /** Account behind the conversation's endpoint; `null` when unresolved, `undefined` while loading. */
  connectedAccountName?: string | null;
  /** `undefined` while conversation details are still loading. */
  customerServiceWindowExpiresAt: string | null | undefined;
  lastInboundCustomerMessageAt?: string | null;
  /** Backend free-form capability for Messenger / Instagram; `undefined` while loading. */
  metaWindowOpen?: boolean;
  contactActionPending?: boolean;
  onSaveContact?: () => void;
  onRestoreContact?: () => void;
  /** Saved contact (groups, other channel profiles); `none` for unsaved or deleted senders. */
  contactDetail?: SavedContactDetailState;
  onRetryContactDetail?: () => void;
  onOpenConversation?: (conversationId: number) => void;
  /** Owners and admins can open the Contacts workspace. */
  canManageContacts?: boolean;
  /** Live assignee chip. Rendered under Status. */
  assigneeControl?: ReactNode;
  /** Verified Private Reply origin; null/omitted when no SocialComment FK link exists. */
  originatingSocialComment?: MessageOriginatingSocialCommentDto | null;
  className?: string;
}): React.JSX.Element {
  const { t } = useTranslation();
  const headingId = useId();
  const nowMs = useNowMs();
  const [openSections, setOpenSections] = useState<Record<DetailsSectionKey, boolean>>(
    () => defaultOpenSections(),
  );
  const [sectionsConversationId, setSectionsConversationId] = useState(conversation.conversationId);
  if (sectionsConversationId !== conversation.conversationId) {
    setSectionsConversationId(conversation.conversationId);
    setOpenSections(defaultOpenSections());
  }
  const title = conversationTitle(conversation, t);
  const profile = conversation.contactPlatformProfile;
  const model = buildConversationDetailsModel({
    title,
    platformType: conversation.platformType,
    status: conversation.status,
    closedAt: conversation.closedAt,
    profile,
    customerServiceWindowExpiresAt,
    lastInboundCustomerMessageAt,
    metaWindowOpen,
    nowMs,
  });
  const channelLabel = t(`channel.${channelLabelI18nKey(conversation.platformType)}`);
  const replyWindow = model.replyWindow;
  const windowTone = replyWindow ? replyWindowTone(replyWindow) : null;
  const savedContact = contactDetail.kind === "ready" ? contactDetail.contact : null;
  const groups = savedContact?.groups ?? [];
  const linkedIdentities = savedContact ? linkedContactIdentities(savedContact.platformProfiles, profile.id) : [];
  const windowRuleKey =
    conversation.platformType === "WHATSAPP"
      ? "inbox.header.windowTipWhatsApp"
      : "inbox.header.windowTipMeta";
  const contactAction =
    model.contactState === "NOT_SAVED" && onSaveContact
      ? { label: t("inbox.saveContact.action"), icon: UserPlusIcon, onClick: onSaveContact, testId: "details-save-contact" }
      : model.contactState === "DELETED" && onRestoreContact
        ? { label: t("inbox.restoreContact.action"), icon: RotateCcwIcon, onClick: onRestoreContact, testId: "details-restore-contact" }
        : null;

  function toggle(key: DetailsSectionKey): void {
    setOpenSections((current) => ({ ...current, [key]: !current[key] }));
  }

  return (
    <div
      className={cn(
        "flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain bg-surface",
        INBOX_SCROLLER_CLASS,
        className,
      )}
    >
      <section
        className="flex flex-col items-center gap-2 border-b border-inbox-line-soft px-4 pt-[18px] pb-4 text-center"
        aria-labelledby={headingId}
      >
        <ConversationAvatar
          title={title}
          imageUrl={profile.platformProfileImageUrl}
          platformType={conversation.platformType}
          toneSeed={profile.id}
          size="xl"
        />
        <div className="w-full min-w-0">
          <h3
            id={headingId}
            className="m-0 truncate text-[0.9375rem] font-semibold tracking-[-0.01em] text-inbox-strong rtl:tracking-normal"
            dir="auto"
          >
            {isPhoneLikeDisplay(title) ? <LtrValue className="truncate">{title}</LtrValue> : title}
          </h3>
          <p className="m-0 mt-1 flex max-w-full items-center justify-center gap-1.5 truncate text-[0.75rem] text-inbox-muted">
            <span className="truncate">{channelLabel}</span>
            {model.secondaryIdentity ? (
              <>
                <span className="text-[#b3c1bd]" aria-hidden="true">
                  ·
                </span>
                <span className="truncate tabular-nums">
                  {isPhoneLikeDisplay(model.secondaryIdentity) ? (
                    <LtrValue className="truncate">{model.secondaryIdentity}</LtrValue>
                  ) : (
                    <bdi>{model.secondaryIdentity}</bdi>
                  )}
                </span>
              </>
            ) : null}
          </p>
        </div>
      </section>

      {originatingSocialComment ? (
        <div className="border-b border-inbox-line-soft px-4 py-3">
          <CommentOriginBanner origin={originatingSocialComment} density="details" />
        </div>
      ) : null}

      <DetailsSection
        title={t("inbox.details.sections.conversation")}
        open={openSections.conversation}
        onToggle={() => toggle("conversation")}
      >
        <dl className="m-0">
        <DetailsRow label={t("inbox.details.fields.channel")}>
          <ChannelIdentityBadge platformType={conversation.platformType} size="row" />
          <span className="truncate">{channelLabel}</span>
        </DetailsRow>
        <DetailsRow label={t("inbox.details.fields.connectedAccount")}>
          {connectedAccountName === undefined ? (
            <span
              className="h-3 w-24 animate-pulse rounded bg-inbox-line-soft motion-reduce:animate-none"
              aria-hidden="true"
            />
          ) : connectedAccountName === null ? (
            <DetailsMissingValue />
          ) : (
            <DetailsValue value={connectedAccountName} ltr={isPhoneLikeDisplay(connectedAccountName)} />
          )}
        </DetailsRow>
        <DetailsRow label={t("inbox.details.fields.conversationStatus")}>
          <DetailsBadge tone={conversationStatusTone(model.conversationStatus)}>
            {t(conversationStatusLabelKey(model.conversationStatus))}
          </DetailsBadge>
        </DetailsRow>
        {assigneeControl ? (
          <DetailsRow label={t("inbox.details.fields.assignee")}>{assigneeControl}</DetailsRow>
        ) : null}
        <DetailsRow label={t("inbox.details.fields.unread")}>
          <span className="tabular-nums">{conversation.unreadCount}</span>
        </DetailsRow>
        {model.closedAt ? (
          <DetailsRow label={t("inbox.details.fields.closedAt")}>
            <DetailsTimestamp iso={model.closedAt} nowMs={nowMs} />
          </DetailsRow>
        ) : null}
        </dl>
      </DetailsSection>

      <DetailsSection
        title={t("inbox.details.sections.contact")}
        open={openSections.contact}
        onToggle={() => toggle("contact")}
      >
        <dl className="m-0">
          <DetailsRow label={t("inbox.details.fields.contactState")}>
            <DetailsBadge tone={contactStateTone(model.contactState)}>
              {t(contactStateLabelKey(model.contactState))}
            </DetailsBadge>
          </DetailsRow>
        </dl>
        <DetailsSubheading>
          {t("inbox.details.providerProfile", { channel: channelLabel })}
        </DetailsSubheading>
        <dl className="m-0">
          {model.providerRows.map((row) => (
            <DetailsFieldRow
              key={row.id}
              label={t(CONTACT_ROW_LABEL_KEY[row.id])}
              value={row.value}
              ltr={row.id !== "platformDisplayName"}
            />
          ))}
        </dl>
        {model.crmRows ? (
          <>
            <DetailsSubheading>{t("inbox.details.crmContact")}</DetailsSubheading>
            <dl className="m-0">
              {model.crmRows.map((row) => (
                <DetailsFieldRow
                  key={row.id}
                  label={t(CONTACT_ROW_LABEL_KEY[row.id])}
                  value={row.value}
                  ltr={false}
                />
              ))}
            </dl>
          </>
        ) : null}
        {contactAction ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 h-8 gap-1.5 rounded-lg border-inbox-line text-[0.78125rem] text-inbox-strong"
            disabled={contactActionPending}
            onClick={contactAction.onClick}
            data-testid={contactAction.testId}
          >
            <contactAction.icon size={15} aria-hidden="true" />
            {contactAction.label}
          </Button>
        ) : null}
      </DetailsSection>

      <DetailsSection
        title={t("inbox.details.sections.groups")}
        count={groups.length}
        open={openSections.groups}
        onToggle={() => toggle("groups")}
      >
        {model.contactState !== "ACTIVE" ? (
          <DetailsNote icon={UsersIcon}>{t(unsavedContactNoteKey("groups", model.contactState))}</DetailsNote>
        ) : contactDetail.kind === "error" ? (
          <ContactDetailError onRetry={onRetryContactDetail} />
        ) : savedContact == null ? (
          <ContactDetailSkeleton shape="chips" />
        ) : (
          <>
            {groups.length > 0 ? (
              <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" data-testid="details-groups">
                {groups.map((group) => (
                  <li
                    key={group.id}
                    className="inline-flex h-[26px] max-w-full items-center gap-1.5 rounded-full border border-inbox-line bg-inbox-field px-2.5 text-[0.78125rem] font-[550] text-[#2d4a42] before:size-1.5 before:shrink-0 before:rounded-full before:bg-brand before:content-['']"
                  >
                    <bdi className="min-w-0 truncate">{group.name}</bdi>
                  </li>
                ))}
              </ul>
            ) : (
              <DetailsNote icon={UsersIcon}>{t("inbox.details.groups.empty")}</DetailsNote>
            )}
            {canManageContacts ? (
              <Link
                to="/contacts"
                className="mt-2 inline-flex items-center gap-1 rounded-md px-1 py-0.5 text-[0.75rem] font-[650] text-primary hover:underline focus-visible:outline-2 focus-visible:outline-primary"
              >
                {t("inbox.details.groups.manage")}
                <ArrowOutIcon size={14} className="rtl:-scale-x-100" aria-hidden="true" />
              </Link>
            ) : null}
          </>
        )}
      </DetailsSection>

      <DetailsSection
        title={t("inbox.details.sections.linked")}
        open={openSections.linked}
        onToggle={() => toggle("linked")}
      >
        {model.contactState !== "ACTIVE" ? (
          <DetailsNote>{t(unsavedContactNoteKey("linked", model.contactState))}</DetailsNote>
        ) : contactDetail.kind === "error" ? (
          <ContactDetailError onRetry={onRetryContactDetail} />
        ) : savedContact == null ? (
          <ContactDetailSkeleton shape="rows" />
        ) : linkedIdentities.length > 0 ? (
          <div data-testid="details-linked-identities">
            {linkedIdentities.map((identity) => (
              <ContactChannelIdentityRow
                key={identity.profileId}
                identity={identity}
                variant="details"
                onOpenConversation={(conversationId) => onOpenConversation?.(conversationId)}
              />
            ))}
          </div>
        ) : (
          <DetailsNote>{t("inbox.details.linked.none")}</DetailsNote>
        )}
      </DetailsSection>

      {replyWindow && windowTone ? (
        <DetailsSection
          title={t("inbox.details.sections.serviceWindow")}
          open={openSections.window}
          onToggle={() => toggle("window")}
        >
          <dl className="m-0">
            <DetailsRow label={t("inbox.details.fields.windowState")}>
              <DetailsBadge tone={windowTone === "closed" ? "muted" : windowTone === "warn" ? "warn" : "ok"}>
                {replyWindow.kind === "OPEN" ? t("inbox.details.window.open") : t("inbox.details.window.closed")}
              </DetailsBadge>
            </DetailsRow>
            {replyWindow.expiresAtMs != null ? (
              <DetailsRow label={t("inbox.details.fields.windowExpires")}>
                <DetailsTimestamp iso={new Date(replyWindow.expiresAtMs).toISOString()} nowMs={nowMs} />
              </DetailsRow>
            ) : null}
            {lastInboundCustomerMessageAt ? (
              <DetailsRow label={t("inbox.details.fields.lastInbound")}>
                <DetailsTimestamp iso={lastInboundCustomerMessageAt} nowMs={nowMs} />
              </DetailsRow>
            ) : null}
          </dl>
          <div className="mt-1.5">
            <DetailsNote>{t(windowRuleKey)}</DetailsNote>
          </div>
        </DetailsSection>
      ) : null}
    </div>
  );
}
